import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useI18n } from '@/i18n';
import { UploadCloud } from 'lucide-react';
import { PageHeader } from '@vanguard-atlas/shared';
import { Button, Card, Select, Icon, Progress, Skeleton } from '@/components/ui';
import { toast } from 'sonner';
import { useCollections, useCollection, collectionKeys } from '@/hooks/use-knowledge';
import { useTenant } from '@/contexts/TenantContext';
import apiClient from '@/lib/api-client';

// =============================================================================
// Types
// =============================================================================

interface UploadResult {
  success: number;
  failed: number;
}

// =============================================================================
// Upload Hook
// =============================================================================

function useUploadDocuments() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async ({
      collectionId,
      files,
      taxonomyTag,
    }: {
      collectionId: string;
      files: File[];
      taxonomyTag?: string;
    }): Promise<UploadResult> => {
      let success = 0;
      let failed = 0;

      // Upload files one by one to handle progress and errors per file
      for (const file of files) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          if (taxonomyTag) {
            formData.append('taxonomy_tags', JSON.stringify([taxonomyTag]));
          }

          await apiClient.post(
            `/api/v1/knowledge/collections/${collectionId}/documents`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          );
          success++;
        } catch (error) {
          console.error(`Failed to upload file "${file.name}":`, error);
          failed++;
        }
      }

      return { success, failed };
    },
    onSuccess: (result, { collectionId }) => {
      queryClient.invalidateQueries({ queryKey: collectionKeys.documents(tenantId, collectionId) });
      queryClient.invalidateQueries({ queryKey: collectionKeys.detail(tenantId, collectionId) });
      
      if (result.failed === 0) {
        toast.success(`Successfully uploaded ${result.success} document${result.success > 1 ? 's' : ''}`);
      } else if (result.success > 0) {
        toast.warning(`Uploaded ${result.success} document${result.success > 1 ? 's' : ''}, ${result.failed} failed`);
      } else {
        toast.error('All uploads failed');
      }
    },
    onError: () => {
      toast.error('Failed to upload documents');
    },
  });
}

// =============================================================================
// Constants
// =============================================================================

// Delay before navigating after successful upload to show 100% progress
const NAVIGATION_DELAY_MS = 500;
// Delay before resetting upload state after completion
const RESET_STATE_DELAY_MS = 500;

// =============================================================================
// Ingest Page
// =============================================================================

export function IngestPage() {
  const { t } = useI18n();
  const { id: collectionIdFromUrl } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [files, setFiles] = useState<File[]>([]);
  const [collection, setCollection] = useState(collectionIdFromUrl || '');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Fetch collection details if ID is from URL
  const { data: collectionDetails } = useCollection(collectionIdFromUrl || '', {
    enabled: !!collectionIdFromUrl,
  });

  // Fetch collections from API
  const { data: collectionsData, isLoading: isLoadingCollections } = useCollections({ page_size: 100 });
  const uploadMutation = useUploadDocuments();

  // Transform collections to options
  const collectionOptions = collectionsData?.data.map((col) => ({
    value: col.id,
    label: col.name,
  })) || [];

  // MVP: Taxonomy selection is not yet implemented
  // Will be connected to the taxonomy API in a future sprint

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (isUploading) return;
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles(prev => [...prev, ...droppedFiles]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && !isUploading) {
      setFiles(prev => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  const handleStartIngest = async () => {
    if (!collection) {
      toast.error('Please select a collection');
      return;
    }
    if (files.length === 0) {
      toast.error('Please select at least one file');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    // Simulate progress updates
    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => Math.min(prev + 10, 90));
    }, 500);

    try {
      await uploadMutation.mutateAsync({
        collectionId: collection,
        files,
        // MVP: taxonomyTag will be added when taxonomy API is implemented
      });
      setUploadProgress(100);
      setFiles([]); // Clear files after successful upload
      
      // Navigate back to the collection if we came from a collection-specific route
      if (collectionIdFromUrl) {
        setTimeout(() => {
          navigate(`/knowledge/collections/${collectionIdFromUrl}`);
        }, NAVIGATION_DELAY_MS);
      }
    } finally {
      clearInterval(progressInterval);
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
      }, RESET_STATE_DELAY_MS);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        {collectionIdFromUrl && (
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(`/knowledge/collections/${collectionIdFromUrl}`)}
          >
            <Icon name="arrow-left" size={20} />
          </Button>
        )}
        <PageHeader
          icon={UploadCloud}
          title={
            collectionDetails
              ? `Upload to ${collectionDetails.name}`
              : t('admin.knowledge.ingest.title')
          }
          subtitle={t('admin.knowledge.ingest.subtitle')}
          testId="ingest-header"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Drop Zone */}
        <div className="lg:col-span-2">
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors bg-white dark:bg-gray-800 ${
              isUploading 
                ? 'border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed' 
                : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
            }`}
          >
            <Icon name="upload" size={48} className="mx-auto text-gray-400 dark:text-gray-500 mb-4" />
            <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
              {t('admin.knowledge.ingest.dropzone.title')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {t('admin.knowledge.ingest.dropzone.subtitle')}
            </p>
            <label className={isUploading ? 'cursor-not-allowed' : 'cursor-pointer'}>
              <input
                type="file"
                multiple
                accept=".md,.txt,.pdf,.docx"
                onChange={handleFileSelect}
                className="hidden"
                disabled={isUploading}
              />
              <span className={`inline-flex items-center justify-center gap-2 h-10 px-4 text-sm font-medium rounded-lg transition-colors ${
                isUploading 
                  ? 'bg-gray-400 text-white cursor-not-allowed' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}>
                {t('admin.knowledge.ingest.dropzone.button')}
              </span>
            </label>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-4">
              {t('admin.knowledge.ingest.dropzone.formats')}
            </p>
          </div>

          {/* Selected Files */}
          {files.length > 0 && (
            <Card className="mt-4 p-4">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">
                {files.length} files selected
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {files.map((file, i) => (
                  <div key={i} className="flex items-center gap-3 py-2 px-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <Icon name="file" size={16} className="text-gray-400" />
                    <span className="flex-1 text-sm text-gray-700 dark:text-gray-300 truncate">{file.name}</span>
                    <span className="text-xs text-gray-400">
                      {(file.size / 1024).toFixed(1)} KB
                    </span>
                    <button
                      onClick={() => setFiles(files.filter((_, idx) => idx !== i))}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                      disabled={isUploading}
                    >
                      <Icon name="x" size={14} className="text-gray-400" />
                    </button>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Upload Progress */}
          {isUploading && (
            <Card className="mt-4 p-4">
              <div className="flex items-center gap-3 mb-2">
                <Icon name="loader" size={16} className="animate-spin text-blue-500" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t('admin.knowledge.ingest.uploadingDocuments')}
                </span>
              </div>
              <Progress value={uploadProgress} className="h-2" />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {uploadProgress}% {t('common.loading').replace('...', '')}
              </p>
            </Card>
          )}
        </div>

        {/* Configuration */}
        <Card className="p-4">
          <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-4">
            {t('admin.knowledge.ingest.config.title')}
          </h3>
          <div className="space-y-4">
            {collectionIdFromUrl && collectionDetails ? (
              // Show fixed collection when navigated from collection page
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  {t('admin.knowledge.ingest.config.targetCollection')}
                </label>
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {collectionDetails.name}
                  </span>
                </div>
              </div>
            ) : isLoadingCollections ? (
              <div className="space-y-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : (
              <Select
                label={t('admin.knowledge.ingest.config.targetCollection')}
                options={collectionOptions}
                value={collection}
                onChange={(e) => setCollection(e.target.value)}
                placeholder="Select collection"
                required
                disabled={isUploading}
              />
            )}
            
            {/* MVP: Taxonomy selector not yet implemented */}
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                <Icon name="info" size={14} className="inline mr-2" />
                Taxonomy tagging will be available in a future update.
              </p>
            </div>
            
            <Button 
              className="w-full" 
              disabled={files.length === 0 || !collection || isUploading}
              onClick={handleStartIngest}
            >
              {isUploading ? (
                <>
                  <Icon name="loader" size={16} className="animate-spin mr-2" />
                  {t('admin.knowledge.ingest.uploading')}
                </>
              ) : (
                t('admin.knowledge.ingest.startIngest')
              )}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
